import type { AthenaAuthDatabase } from "../../auth/local/database.ts";
import { AthenaAuthRuntimeError } from "../../auth/local/errors.ts";
import type { AuthMemberRow } from "../../auth/local/models.ts";
import {
	FOUNDING_OWNER_REMOVE_FORBIDDEN,
	FOUNDING_OWNER_ROLE_LOCKED,
} from "../../auth/local/organization-invariants.ts";
import { type AthenaRightKey, parseAthenaRightKey } from "../../rights/key.ts";
import { capabilitiesFromRights } from "./capabilities.ts";
import {
	AUTHORIZATION_CATALOG_VERSION,
	listAthenaAuthorizationRights,
} from "./catalog.ts";
import { assertCloneRoleTenantBoundary } from "./clone-source.ts";
import { toRoleDescriptor, toRoleDetail } from "./role-descriptor.ts";
import {
	assertProtectedRoleImmutable,
	assertRightsReplaceAllowed,
	throwReassignmentInvalid,
	throwRoleHasAssignments,
	throwRoleNotFound,
	throwRoleVersionConflict,
} from "./role-invariants.ts";
import { projectAssignableRoles } from "./snapshot-catalog.ts";
import type { AthenaAuthorizationStore } from "./store.ts";
import {
	BUILTIN_AUTHORIZATION_ROLES,
	mapLegacyMemberRole,
	mapLegacyUserRole,
	ORGANIZATION_OWNER_ROLE,
	resolveMemberAssignmentRole,
} from "./templates.ts";
import type {
	AssignedRoleSummary,
	AthenaAuthorizationRoleDetail,
	AthenaAuthorizationRoleRecord,
	AuthorizationSnapshot,
	MemberAuthorizationActions,
	RoleDescriptor,
} from "./types.ts";

interface RoleRow {
	assignable: boolean;
	id: string;
	key: string;
	name: string;
	organization_id: string | null;
	protected: boolean;
	scope_kind: "platform" | "organization";
	system_kind: "owner" | "admin" | "member" | null;
	version: number;
}

function mapRoleRow(row: RoleRow): AthenaAuthorizationRoleRecord {
	return {
		assignable: row.assignable,
		id: row.id,
		key: row.key,
		name: row.name,
		organizationId: row.organization_id,
		protected: row.protected,
		scopeKind: row.scope_kind,
		systemKind: row.system_kind,
		version: row.version,
	};
}

export class PostgresAuthorizationStore implements AthenaAuthorizationStore {
	constructor(private readonly db: AthenaAuthDatabase) { }

	async materialize(): Promise<void> {
		for (const right of listAthenaAuthorizationRights()) {
			await this.db.query(
				`INSERT INTO athena.authorization_rights (
					key, domain, display_name, description, scope_kind, risk_level,
					assignable, source_version
				) VALUES ($1, $2, $3, $4, $5, $6, $7, $8)
				ON CONFLICT (key) DO UPDATE SET
					domain = EXCLUDED.domain,
					display_name = EXCLUDED.display_name,
					description = EXCLUDED.description,
					scope_kind = EXCLUDED.scope_kind,
					risk_level = EXCLUDED.risk_level,
					assignable = EXCLUDED.assignable,
					source_version = EXCLUDED.source_version`,
				[
					right.key,
					right.domain,
					right.displayName,
					right.description,
					right.scopeKind,
					right.riskLevel,
					right.assignable,
					AUTHORIZATION_CATALOG_VERSION,
				],
			);
		}
		for (const template of BUILTIN_AUTHORIZATION_ROLES) {
			await this.db.query(
				`INSERT INTO athena.authorization_roles (
					id, key, name, scope_kind, organization_id, system_kind,
					protected, assignable, version
				) VALUES ($1, $2, $3, $4, NULL, $5, $6, $7, 1)
				ON CONFLICT (id) DO UPDATE SET
					name = EXCLUDED.name,
					scope_kind = EXCLUDED.scope_kind,
					system_kind = EXCLUDED.system_kind,
					protected = EXCLUDED.protected,
					assignable = EXCLUDED.assignable,
					updated_at = NOW()`,
				[
					template.id,
					template.key,
					template.name,
					template.scopeKind,
					template.systemKind,
					template.protected,
					template.assignable,
				],
			);
			await this.db.query(
				`DELETE FROM athena.authorization_role_rights WHERE role_id = $1`,
				[template.id],
			);
			for (const key of template.rights) {
				await this.db.query(
					`INSERT INTO athena.authorization_role_rights (role_id, right_key)
					 VALUES ($1, $2)
					 ON CONFLICT DO NOTHING`,
					[template.id, key],
				);
			}
		}
		await this.db.query(
			`INSERT INTO athena.authorization_user_roles (user_id, role_id)
			 SELECT u.id, r.id
			 FROM athena.users u
			 JOIN athena.authorization_roles r
			   ON r.id = $1
			  AND r.organization_id IS NULL
			 WHERE COALESCE(u.role, '') = 'admin'
			   AND NOT EXISTS (
			     SELECT 1 FROM athena.authorization_user_roles a WHERE a.user_id = u.id
			   )`,
			["platform_admin"],
		);
		await this.db.query(
			`INSERT INTO athena.authorization_user_roles (user_id, role_id)
			 SELECT u.id, r.id
			 FROM athena.users u
			 JOIN athena.authorization_roles r
			   ON r.id = $1
			  AND r.organization_id IS NULL
			 WHERE COALESCE(u.role, '') = 'unauthorized'
			   AND NOT EXISTS (
			     SELECT 1 FROM athena.authorization_user_roles a WHERE a.user_id = u.id
			   )`,
			["platform_unauthorized"],
		);
		await this.db.query(
			`INSERT INTO athena.authorization_user_roles (user_id, role_id)
			 SELECT u.id, r.id
			 FROM athena.users u
			 JOIN athena.authorization_roles r
			   ON r.id = $1
			  AND r.organization_id IS NULL
			 WHERE COALESCE(u.role, '') IN ('', 'customer')
			   AND NOT EXISTS (
			     SELECT 1 FROM athena.authorization_user_roles a WHERE a.user_id = u.id
			   )`,
			["platform_customer"],
		);
		await this.db.query(
			`INSERT INTO athena.authorization_user_roles (user_id, role_id)
			 SELECT u.id, r.id
			 FROM athena.users u
			 JOIN athena.authorization_roles r
			   ON r.id = $1
			  AND r.organization_id IS NULL
			 WHERE u.role IS NOT NULL
			   AND u.role NOT IN ('admin', 'unauthorized', 'customer')
			   AND NOT EXISTS (
			     SELECT 1 FROM athena.authorization_user_roles a WHERE a.user_id = u.id
			   )`,
			["platform_unauthorized"],
		);
		await this.db.query(
			`INSERT INTO athena.authorization_member_roles (member_id, role_id)
			 SELECT m.id, r.id
			 FROM athena.member m
			 JOIN athena.authorization_roles r
			   ON r.id = CASE
			     WHEN m.role = 'owner' THEN 'organization_owner'
			     WHEN m.role = 'admin' THEN 'organization_admin'
			     ELSE 'organization_member'
			   END
			 WHERE NOT EXISTS (
			   SELECT 1 FROM athena.authorization_member_roles a WHERE a.member_id = m.id
			 )`,
		);
	}

	async hasUserAssignment(userId: string): Promise<boolean> {
		const result = await this.db.query(
			`SELECT 1 FROM athena.authorization_user_roles WHERE user_id = $1`,
			[userId],
		);
		return result.rowCount > 0;
	}

	async assignUserRole(
		userId: string,
		roleKey: string,
		assignedBy?: string,
	): Promise<void> {
		const mapped = mapLegacyUserRole(
			roleKey === "admin" ||
				roleKey === "customer" ||
				roleKey === "unauthorized"
				? roleKey
				: roleKey,
		);
		const key = roleKey.startsWith("platform_") ? roleKey : mapped;
		const inserted = await this.db.query(
			`INSERT INTO athena.authorization_user_roles (user_id, role_id, assigned_by)
			 SELECT $1, id, $3
			 FROM athena.authorization_roles
			 WHERE key = $2 AND organization_id IS NULL AND scope_kind = 'platform'
			 ON CONFLICT (user_id) DO UPDATE SET
			   role_id = EXCLUDED.role_id,
			   assigned_by = EXCLUDED.assigned_by,
			   updated_at = NOW()`,
			[userId, key, assignedBy ?? null],
		);
		if (inserted.rowCount < 1) {
			throw AthenaAuthRuntimeError.badRequest("Unknown platform role");
		}
		await this.bumpRevision(null);
		await this.writeAudit({
			action: "user.role.assign",
			actorUserId: assignedBy,
			after: { roleKey: key },
			targetId: userId,
			targetKind: "user",
		});
	}

	async assignMemberRole(
		memberId: string,
		roleKey: string,
		assignedBy?: string,
		organizationId?: string | null,
	): Promise<void> {
		const member = await this.db.query<{ organization_id: string }>(
			`SELECT organization_id FROM athena.member WHERE id = $1`,
			[memberId],
		);
		const orgId = organizationId ?? member.rows[0]?.organization_id ?? null;
		if (!orgId) {
			throw AthenaAuthRuntimeError.badRequest("Member not found");
		}
		const role = await this.lookupAssignableOrganizationRole(orgId, roleKey);
		if (!role) {
			throw AthenaAuthRuntimeError.badRequest("Unknown or unassignable role");
		}
		await this.db.query(
			`INSERT INTO athena.authorization_member_roles (member_id, role_id, assigned_by)
			 VALUES ($1, $2, $3)
			 ON CONFLICT (member_id) DO UPDATE SET
			   role_id = EXCLUDED.role_id,
			   assigned_by = EXCLUDED.assigned_by,
			   updated_at = NOW()`,
			[memberId, role.id, assignedBy ?? null],
		);
		await this.bumpRevision(orgId);
		await this.writeAudit({
			action: "member.role.assign",
			actorUserId: assignedBy,
			after: { roleKey: role.key },
			organizationId: orgId,
			targetId: memberId,
			targetKind: "member",
		});
	}

	async lookupAssignableOrganizationRole(
		organizationId: string,
		roleRef: string,
	): Promise<AthenaAuthorizationRoleRecord | undefined> {
		const mapped = resolveMemberAssignmentRole(roleRef);
		const result = await this.db.query<RoleRow>(
			`SELECT * FROM athena.authorization_roles
			 WHERE scope_kind = 'organization'
			   AND assignable = TRUE
			   AND (
			     id = $2
			     OR key = $2
			     OR key = $3
			     OR ($2 IN ('owner', 'organization_owner') AND key = 'organization_owner')
			     OR ($2 IN ('admin', 'organization_admin') AND key = 'organization_admin')
			     OR ($2 IN ('member', 'organization_member') AND key = 'organization_member')
			   )
			   AND (organization_id IS NULL OR organization_id = $1)
			 ORDER BY CASE WHEN organization_id IS NOT NULL THEN 0 ELSE 1 END
			 LIMIT 1`,
			[organizationId, roleRef, mapped],
		);
		const row = result.rows[0];
		if (!row) {
			return undefined;
		}
		return mapRoleRow(row);
	}

	async cloneRole(input: {
		actorUserId: string;
		name: string;
		organizationId?: string | null;
		sourceRoleId: string;
	}): Promise<AthenaAuthorizationRoleRecord> {
		return this.db.transaction(async (tx) => {
			const nested = new PostgresAuthorizationStore(tx);
			return nested.cloneRoleInTransaction(input);
		});
	}

	private async cloneRoleInTransaction(input: {
		actorUserId: string;
		name: string;
		organizationId?: string | null;
		sourceRoleId: string;
	}): Promise<AthenaAuthorizationRoleRecord> {
		const id = crypto.randomUUID();
		const source = await this.db.query<RoleRow>(
			`SELECT * FROM athena.authorization_roles WHERE id = $1`,
			[input.sourceRoleId],
		);
		const row = source.rows[0];
		if (!row) {
			throw AthenaAuthRuntimeError.badRequest("Source role not found");
		}
		const organizationId = input.organizationId ?? row.organization_id;
		assertCloneRoleTenantBoundary({
			sourceOrganizationId: row.organization_id,
			sourceScopeKind: row.scope_kind,
			targetOrganizationId: organizationId,
		});
		const key = `custom_${id.slice(0, 8)}`;
		const name = input.name.trim() || `${row.name} copy`;
		await this.db.query(
			`INSERT INTO athena.authorization_roles (
				id, key, name, scope_kind, organization_id, system_kind,
				protected, assignable, version
			) VALUES ($1, $2, $3, $4, $5, NULL, FALSE, TRUE, 1)`,
			[id, key, name, row.scope_kind, organizationId],
		);
		await this.db.query(
			`INSERT INTO athena.authorization_role_rights (role_id, right_key)
			 SELECT $1, right_key FROM athena.authorization_role_rights WHERE role_id = $2`,
			[id, input.sourceRoleId],
		);
		await this.bumpRevision(organizationId);
		const clonedRights = await this.listRoleRights(id);
		await this.writeAudit({
			action: "role.clone",
			actorUserId: input.actorUserId,
			after: { rights: [...clonedRights] },
			organizationId,
			targetId: id,
			targetKind: "role",
		});
		return {
			assignable: true,
			id,
			key,
			name,
			organizationId,
			protected: false,
			scopeKind: row.scope_kind,
			systemKind: null,
			version: 1,
		};
	}

	async createRole(input: {
		actorUserId: string;
		name: string;
		organizationId?: string | null;
		rights?: readonly string[];
		scopeKind: "platform" | "organization";
	}): Promise<AthenaAuthorizationRoleRecord> {
		return this.db.transaction(async (tx) => {
			const nested = new PostgresAuthorizationStore(tx);
			return nested.createRoleInTransaction(input);
		});
	}

	async getRole(
		id: string,
		organizationId?: string | null,
	): Promise<AthenaAuthorizationRoleDetail | undefined> {
		const role = await this.loadScopedRole(id, organizationId);
		if (!role) {
			return undefined;
		}
		const rights = await this.listRoleRights(role.id);
		return toRoleDetail(role, rights, await this.countAssignments(role));
	}

	async listRoles(input: {
		organizationId?: string | null;
		scopeKind: "platform" | "organization";
	}): Promise<readonly RoleDescriptor[]> {
		const result = input.organizationId
			? await this.db.query<
					RoleRow & { assignment_count: string; right_count: string }
				>(
					`SELECT r.*,
					        (SELECT COUNT(*)::text FROM athena.authorization_member_roles mr WHERE mr.role_id = r.id) AS assignment_count,
					        (SELECT COUNT(*)::text FROM athena.authorization_role_rights rr WHERE rr.role_id = r.id) AS right_count
					 FROM athena.authorization_roles r
					 WHERE r.scope_kind = 'organization'
					   AND (r.organization_id IS NULL OR r.organization_id = $1)
					 ORDER BY r.protected DESC, r.key`,
					[input.organizationId],
				)
			: await this.db.query<
					RoleRow & { assignment_count: string; right_count: string }
				>(
					`SELECT r.*,
					        (SELECT COUNT(*)::text FROM athena.authorization_user_roles ur WHERE ur.role_id = r.id) AS assignment_count,
					        (SELECT COUNT(*)::text FROM athena.authorization_role_rights rr WHERE rr.role_id = r.id) AS right_count
					 FROM athena.authorization_roles r
					 WHERE r.scope_kind = 'platform' AND r.organization_id IS NULL
					 ORDER BY r.protected DESC, r.key`,
				);
		return result.rows
			.filter((row) => row.scope_kind === input.scopeKind)
			.map((row) =>
				toRoleDescriptor(mapRoleRow(row), {
					assignmentCount: Number(row.assignment_count),
					rightCount: Number(row.right_count),
				}),
			);
	}

	async listRoleRights(roleId: string): Promise<readonly AthenaRightKey[]> {
		const result = await this.db.query<{ right_key: string }>(
			`SELECT right_key FROM athena.authorization_role_rights
			 WHERE role_id = $1 ORDER BY right_key`,
			[roleId],
		);
		return result.rows.map((row) => parseAthenaRightKey(row.right_key));
	}

	async updateRole(input: {
		actorUserId: string;
		expectedVersion: number;
		id: string;
		name: string;
		organizationId?: string | null;
	}): Promise<AthenaAuthorizationRoleRecord> {
		return this.db.transaction(async (tx) => {
			const nested = new PostgresAuthorizationStore(tx);
			return nested.updateRoleInTransaction(input);
		});
	}

	async replaceRoleRights(input: {
		actorUserId: string;
		expectedVersion: number;
		id: string;
		organizationId?: string | null;
		rights: readonly string[];
	}): Promise<AthenaAuthorizationRoleRecord> {
		return this.db.transaction(async (tx) => {
			const nested = new PostgresAuthorizationStore(tx);
			return nested.replaceRoleRightsInTransaction(input);
		});
	}

	async deleteRole(input: {
		actorUserId: string;
		expectedVersion: number;
		id: string;
		organizationId?: string | null;
		reassignmentRoleId?: string | null;
	}): Promise<void> {
		await this.db.transaction(async (tx) => {
			const nested = new PostgresAuthorizationStore(tx);
			await nested.deleteRoleInTransaction(input);
		});
	}

	private async createRoleInTransaction(input: {
		actorUserId: string;
		name: string;
		organizationId?: string | null;
		rights?: readonly string[];
		scopeKind: "platform" | "organization";
	}): Promise<AthenaAuthorizationRoleRecord> {
		const name = input.name.trim();
		if (!name) {
			throw AthenaAuthRuntimeError.badRequest("name is required");
		}
		if (input.scopeKind === "organization" && !input.organizationId) {
			throw AthenaAuthRuntimeError.badRequest("organizationId is required");
		}
		const rights = input.rights ?? [];
		assertRightsReplaceAllowed({
			organizationId: input.organizationId,
			rights,
			roleScope: input.scopeKind,
		});
		const id = crypto.randomUUID();
		const key = `custom_${id.slice(0, 8)}`;
		const organizationId =
			input.scopeKind === "organization" ? (input.organizationId ?? null) : null;
		await this.db.query(
			`INSERT INTO athena.authorization_roles (
				id, key, name, scope_kind, organization_id, system_kind,
				protected, assignable, version
			) VALUES ($1, $2, $3, $4, $5, NULL, FALSE, TRUE, 1)`,
			[id, key, name, input.scopeKind, organizationId],
		);
		await this.replaceMapping(id, rights);
		await this.bumpRevision(organizationId);
		await this.writeAudit({
			action: "role.create",
			actorUserId: input.actorUserId,
			after: { rights: [...rights].sort() },
			organizationId,
			targetId: id,
			targetKind: "role",
		});
		return {
			assignable: true,
			id,
			key,
			name,
			organizationId,
			protected: false,
			scopeKind: input.scopeKind,
			systemKind: null,
			version: 1,
		};
	}

	private async updateRoleInTransaction(input: {
		actorUserId: string;
		expectedVersion: number;
		id: string;
		name: string;
		organizationId?: string | null;
	}): Promise<AthenaAuthorizationRoleRecord> {
		const role = await this.requireScopedRole(input.id, input.organizationId);
		assertProtectedRoleImmutable(role);
		const name = input.name.trim();
		if (!name) {
			throw AthenaAuthRuntimeError.badRequest("name is required");
		}
		const updated = await this.bumpRoleVersion(role.id, input.expectedVersion);
		await this.db.query(
			`UPDATE athena.authorization_roles SET name = $1, updated_at = NOW() WHERE id = $2`,
			[name, role.id],
		);
		await this.bumpRevision(role.organizationId);
		await this.writeAudit({
			action: "role.rename",
			actorUserId: input.actorUserId,
			after: { name },
			before: { name: role.name },
			organizationId: role.organizationId,
			targetId: role.id,
			targetKind: "role",
		});
		return { ...updated, name };
	}

	private async replaceRoleRightsInTransaction(input: {
		actorUserId: string;
		expectedVersion: number;
		id: string;
		organizationId?: string | null;
		rights: readonly string[];
	}): Promise<AthenaAuthorizationRoleRecord> {
		const role = await this.requireScopedRole(input.id, input.organizationId);
		assertProtectedRoleImmutable(role);
		assertRightsReplaceAllowed({
			organizationId: role.organizationId,
			rights: input.rights,
			roleScope: role.scopeKind,
		});
		const before = [...(await this.listRoleRights(role.id))].sort();
		const after = [...new Set(input.rights)].sort();
		const updated = await this.bumpRoleVersion(role.id, input.expectedVersion);
		await this.replaceMapping(role.id, after);
		await this.bumpRevision(role.organizationId);
		await this.writeAudit({
			action: "role.rights.replace",
			actorUserId: input.actorUserId,
			after: { rights: after },
			before: { rights: before },
			organizationId: role.organizationId,
			targetId: role.id,
			targetKind: "role",
		});
		return updated;
	}

	private async deleteRoleInTransaction(input: {
		actorUserId: string;
		expectedVersion: number;
		id: string;
		organizationId?: string | null;
		reassignmentRoleId?: string | null;
	}): Promise<void> {
		const role = await this.requireScopedRole(input.id, input.organizationId);
		assertProtectedRoleImmutable(role);
		await this.bumpRoleVersion(role.id, input.expectedVersion);
		const assigned = await this.countAssignments(role);
		if (assigned > 0) {
			const replacementId = input.reassignmentRoleId?.trim() ?? "";
			if (!replacementId) {
				throwRoleHasAssignments();
			}
			const replacement = await this.requireScopedRole(
				replacementId,
				input.organizationId,
			);
			if (
				replacement.id === role.id ||
				!replacement.assignable ||
				replacement.scopeKind !== role.scopeKind
			) {
				throwReassignmentInvalid();
			}
			if (role.scopeKind === "organization") {
				await this.db.query(
					`UPDATE athena.authorization_member_roles SET role_id = $1, updated_at = NOW()
					 WHERE role_id = $2`,
					[replacement.id, role.id],
				);
			} else {
				await this.db.query(
					`UPDATE athena.authorization_user_roles SET role_id = $1, updated_at = NOW()
					 WHERE role_id = $2`,
					[replacement.id, role.id],
				);
			}
		}
		const before = [...(await this.listRoleRights(role.id))].sort();
		await this.db.query(
			`DELETE FROM athena.authorization_roles WHERE id = $1`,
			[role.id],
		);
		await this.bumpRevision(role.organizationId);
		await this.writeAudit({
			action: "role.delete",
			actorUserId: input.actorUserId,
			before: { rights: before },
			organizationId: role.organizationId,
			targetId: role.id,
			targetKind: "role",
		});
	}

	async listAudit(input: {
		limit?: number;
		organizationId?: string | null;
	}): Promise<readonly Record<string, unknown>[]> {
		const result = await this.db.query<Record<string, unknown>>(
			`SELECT * FROM athena.authorization_audit_log
			 WHERE (
			   ($1::text IS NULL AND organization_id IS NULL)
			   OR ($1::text IS NOT NULL AND organization_id = $1)
			 )
			 ORDER BY created_at DESC
			 LIMIT $2`,
			[input.organizationId ?? null, input.limit ?? 50],
		);
		return result.rows;
	}

	async resolveEffectiveRights(input: {
		activeOrganizationId?: string | null;
		getMember: (
			organizationId: string,
			userId: string,
		) => Promise<AuthMemberRow | undefined>;
		userId: string;
	}): Promise<readonly AthenaRightKey[]> {
		const platform = await this.db.query<{ right_key: string }>(
			`SELECT rr.right_key
			 FROM athena.authorization_user_roles ur
			 JOIN athena.authorization_role_rights rr ON rr.role_id = ur.role_id
			 WHERE ur.user_id = $1`,
			[input.userId],
		);
		const keys = new Set(platform.rows.map((row) => row.right_key));
		const organizationId = input.activeOrganizationId?.trim() ?? "";
		if (organizationId.length > 0) {
			const member = await input.getMember(organizationId, input.userId);
			if (member) {
				const org = await this.db.query<{ right_key: string }>(
					`SELECT rr.right_key
					 FROM athena.authorization_member_roles mr
					 JOIN athena.authorization_role_rights rr ON rr.role_id = mr.role_id
					 WHERE mr.member_id = $1`,
					[member.id],
				);
				for (const row of org.rows) {
					keys.add(row.right_key);
				}
			}
		}
		return [...keys].sort().map((key) => parseAthenaRightKey(key));
	}

	async readSnapshot(input: {
		activeOrganizationId?: string | null;
		foundingOwnerUserId?: string | null;
		getMember: (
			organizationId: string,
			userId: string,
		) => Promise<AuthMemberRow | undefined>;
		listMembers: (organizationId: string) => Promise<AuthMemberRow[]>;
		userId: string;
	}): Promise<AuthorizationSnapshot> {
		void input.foundingOwnerUserId;
		void input.listMembers;
		const effectiveRights = await this.resolveEffectiveRights(input);
		const capabilities = capabilitiesFromRights(effectiveRights);
		const roleRows = await this.db.query<{
			key: string;
			name: string;
			scope_kind: "platform" | "organization";
		}>(
			`SELECT r.key, r.name, r.scope_kind
			 FROM athena.authorization_user_roles ur
			 JOIN athena.authorization_roles r ON r.id = ur.role_id
			 WHERE ur.user_id = $1
			 UNION ALL
			 SELECT r.key, r.name, r.scope_kind
			 FROM athena.member m
			 JOIN athena.authorization_member_roles mr ON mr.member_id = m.id
			 JOIN athena.authorization_roles r ON r.id = mr.role_id
			 WHERE m.user_id = $1 AND $2::text IS NOT NULL AND m.organization_id = $2`,
			[input.userId, input.activeOrganizationId ?? null],
		);
		const roles: AssignedRoleSummary[] = roleRows.rows.map((row) => ({
			displayName: row.name,
			key: row.key,
			scopeKind: row.scope_kind,
		}));
		const assignable = await this.db.query<{
			assignable: boolean;
			assignment_count: string;
			id: string;
			key: string;
			name: string;
			organization_id: string | null;
			protected: boolean;
			right_count: string;
			scope_kind: "platform" | "organization";
			system_kind: "owner" | "admin" | "member" | null;
			version: number;
		}>(
			`SELECT r.id, r.key, r.name, r.scope_kind, r.organization_id, r.protected, r.assignable, r.system_kind, r.version,
			        (
			          CASE
			            WHEN r.scope_kind = 'organization' THEN (
			              SELECT COUNT(*)::text
			              FROM athena.authorization_member_roles mr
			              JOIN athena.member m ON m.id = mr.member_id
			              WHERE mr.role_id = r.id
			                AND ($1::text IS NULL OR m.organization_id = $1)
			            )
			            ELSE (
			              SELECT COUNT(*)::text FROM athena.authorization_user_roles ur WHERE ur.role_id = r.id
			            )
			          END
			        ) AS assignment_count,
			        (
			          SELECT COUNT(*)::text FROM athena.authorization_role_rights rr WHERE rr.role_id = r.id
			        ) AS right_count
			 FROM athena.authorization_roles r
			 WHERE r.assignable = TRUE
			   AND (r.organization_id IS NULL OR r.organization_id = $1)
			 ORDER BY r.scope_kind, r.key`,
			[input.activeOrganizationId ?? null],
		);
		const assignableRoles = projectAssignableRoles({
			activeOrganizationId: input.activeOrganizationId,
			capabilities,
			roles: assignable.rows.map((row) =>
				toRoleDescriptor(
					{
						assignable: row.assignable,
						id: row.id,
						key: row.key,
						name: row.name,
						organizationId: row.organization_id,
						protected: row.protected,
						scopeKind: row.scope_kind,
						systemKind: row.system_kind,
						version: row.version,
					},
					{
						assignmentCount: Number(row.assignment_count),
						rightCount: Number(row.right_count),
					},
				),
			),
		});
		const organizationId = input.activeOrganizationId?.trim() ?? "";
		const revisionRow = await this.db.query<{ revision: string }>(
			organizationId.length > 0
				? `SELECT revision::text AS revision FROM athena.authorization_revisions
				   WHERE scope_kind = 'organization' AND organization_id = $1`
				: `SELECT revision::text AS revision FROM athena.authorization_revisions
				   WHERE scope_kind = 'platform' AND organization_id IS NULL`,
			organizationId.length > 0 ? [organizationId] : [],
		);
		return {
			...(organizationId.length > 0
				? { activeOrganizationId: organizationId }
				: {}),
			assignableRoles,
			capabilities,
			effectiveRights,
			revision: Number(revisionRow.rows[0]?.revision ?? 1),
			roles,
		};
	}

	async annotateMember(
		member: AuthMemberRow,
		actor: {
			foundingOwnerUserId: string | null;
			getMember: (
				organizationId: string,
				userId: string,
			) => Promise<AuthMemberRow | undefined>;
			userId: string;
		},
	): Promise<MemberAuthorizationActions> {
		const roleRow = await this.db.query<{ key: string; name: string }>(
			`SELECT r.key, r.name
			 FROM athena.authorization_member_roles mr
			 JOIN athena.authorization_roles r ON r.id = mr.role_id
			 WHERE mr.member_id = $1`,
			[member.id],
		);
		const roleKey = roleRow.rows[0]?.key ?? mapLegacyMemberRole(member.role);
		const roleName = roleRow.rows[0]?.name ?? roleKey;
		const actorRights = await this.resolveEffectiveRights({
			activeOrganizationId: member.organization_id,
			getMember: actor.getMember,
			userId: actor.userId,
		});
		const capabilities = capabilitiesFromRights(actorRights);
		const denialReasons: MemberAuthorizationActions["denialReasons"] = {};
		if (actor.foundingOwnerUserId === member.user_id) {
			denialReasons.changeRole = FOUNDING_OWNER_ROLE_LOCKED;
			denialReasons.remove = FOUNDING_OWNER_REMOVE_FORBIDDEN;
		} else if (
			actor.userId === member.user_id &&
			roleKey === ORGANIZATION_OWNER_ROLE
		) {
			denialReasons.changeRole =
				"Owners can't change their own role from the members table.";
			denialReasons.remove =
				"Owners can't suspend or remove themselves from the members table.";
		} else if (!capabilities.canChangeMemberRole) {
			denialReasons.changeRole = "Not allowed to change member roles.";
			denialReasons.remove = "Not allowed to remove members.";
		}
		return {
			canChangeRole: denialReasons.changeRole === undefined,
			canRemove: denialReasons.remove === undefined,
			denialReasons,
			roleDisplayName: roleName,
			roleKey,
		};
	}

	private async loadScopedRole(
		id: string,
		organizationId?: string | null,
	): Promise<AthenaAuthorizationRoleRecord | undefined> {
		const result = organizationId
			? await this.db.query<RoleRow>(
					`SELECT * FROM athena.authorization_roles
					 WHERE id = $1 AND scope_kind = 'organization'
					   AND (organization_id IS NULL OR organization_id = $2)`,
					[id, organizationId],
				)
			: await this.db.query<RoleRow>(
					`SELECT * FROM athena.authorization_roles
					 WHERE id = $1 AND scope_kind = 'platform' AND organization_id IS NULL`,
					[id],
				);
		const row = result.rows[0];
		return row ? mapRoleRow(row) : undefined;
	}

	private async requireScopedRole(
		id: string,
		organizationId?: string | null,
	): Promise<AthenaAuthorizationRoleRecord> {
		const role = await this.loadScopedRole(id, organizationId);
		if (!role) {
			throwRoleNotFound();
		}
		return role;
	}

	private async countAssignments(
		role: Pick<AthenaAuthorizationRoleRecord, "id" | "scopeKind">,
	): Promise<number> {
		if (role.scopeKind === "organization") {
			const result = await this.db.query<{ count: string }>(
				`SELECT COUNT(*)::text AS count FROM athena.authorization_member_roles WHERE role_id = $1`,
				[role.id],
			);
			return Number(result.rows[0]?.count ?? 0);
		}
		const result = await this.db.query<{ count: string }>(
			`SELECT COUNT(*)::text AS count FROM athena.authorization_user_roles WHERE role_id = $1`,
			[role.id],
		);
		return Number(result.rows[0]?.count ?? 0);
	}

	private async replaceMapping(
		roleId: string,
		rights: readonly string[],
	): Promise<void> {
		await this.db.query(
			`DELETE FROM athena.authorization_role_rights WHERE role_id = $1`,
			[roleId],
		);
		for (const key of rights) {
			await this.db.query(
				`INSERT INTO athena.authorization_role_rights (role_id, right_key)
				 VALUES ($1, $2)`,
				[roleId, parseAthenaRightKey(key)],
			);
		}
	}

	private async bumpRoleVersion(
		id: string,
		expectedVersion: number,
	): Promise<AthenaAuthorizationRoleRecord> {
		const updated = await this.db.query<RoleRow>(
			`UPDATE athena.authorization_roles
			 SET version = version + 1, updated_at = NOW()
			 WHERE id = $1 AND version = $2
			 RETURNING *`,
			[id, expectedVersion],
		);
		const row = updated.rows[0];
		if (!row) {
			throwRoleVersionConflict();
		}
		return mapRoleRow(row);
	}

	private async bumpRevision(organizationId: string | null): Promise<void> {
		if (organizationId) {
			await this.db.query(
				`WITH updated AS (
					UPDATE athena.authorization_revisions
					SET revision = revision + 1, updated_at = NOW()
					WHERE organization_id = $1
					RETURNING organization_id
				)
				INSERT INTO athena.authorization_revisions (scope_kind, organization_id, revision)
				SELECT 'organization', $1, 1
				WHERE NOT EXISTS (SELECT 1 FROM updated)`,
				[organizationId],
			);
			return;
		}
		await this.db.query(
			`UPDATE athena.authorization_revisions
			 SET revision = revision + 1, updated_at = NOW()
			 WHERE scope_kind = 'platform' AND organization_id IS NULL`,
		);
	}

	private async writeAudit(input: {
		action: string;
		actorUserId?: string;
		after?: unknown;
		before?: unknown;
		organizationId?: string | null;
		targetId: string;
		targetKind: string;
	}): Promise<void> {
		await this.db.query(
			`INSERT INTO athena.authorization_audit_log (
				id, actor_user_id, action, target_kind, target_id, organization_id,
				before_state, after_state, created_at
			) VALUES ($1, $2, $3, $4, $5, $6, $7::jsonb, $8::jsonb, NOW())`,
			[
				crypto.randomUUID(),
				input.actorUserId ?? null,
				input.action,
				input.targetKind,
				input.targetId,
				input.organizationId ?? null,
				JSON.stringify(input.before ?? {}),
				JSON.stringify(input.after ?? {}),
			],
		);
	}
}
